import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Code, Globe, Cpu, Smartphone, Layers, Zap, Briefcase } from "lucide-react";
import MarketplaceV2 from "@/components/marketplace/MarketplaceV2";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const SERVICES = [
  { icon: Globe, title: "Custom Websites", desc: "Premium, conversion-optimized websites built with cutting-edge technology." },
  { icon: Smartphone, title: "Mobile Apps", desc: "Native and cross-platform apps that deliver exceptional user experiences." },
  { icon: Cpu, title: "AI Solutions", desc: "Custom AI chatbots, automation, computer vision, and predictive analytics." },
  { icon: Code, title: "Software Dev", desc: "Enterprise-grade systems, APIs, and cloud infrastructure built for scale." },
  { icon: Layers, title: "E-Commerce", desc: "Full-stack online stores with payments, inventory, and analytics." },
  { icon: Zap, title: "Robotics & IoT", desc: "Smart systems, sensor networks, and automated workflows." },
];

export default function CartBlancJAPage() {
  return (
    <div data-testid="cartblancja-page" className="pt-20 min-h-screen bg-luxury-black">
      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"><div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blue-500 rounded-full blur-[200px]" /></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial="hidden" animate="visible">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body uppercase tracking-[0.3em] text-blue-400 mb-6">CartBlancJA</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-none mb-4">
              Where Technology Meets <span className="italic text-blue-400">Vision.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/40 font-body text-base sm:text-lg max-w-xl mb-8">
              Premium software, websites, mobile apps, AI solutions — and the world's first marketplace where you post any job, freelancers bid, escrow handles payment, and ARIA-powered AI agents can deliver the work themselves.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
              <a href="https://wa.me/18764390976?text=Hi%20CartBlancJA!%20I'm%20interested%20in%20your%20tech%20services." target="_blank" rel="noopener noreferrer" data-testid="cartblancja-cta" className="inline-flex items-center gap-2 bg-blue-500 text-white font-body font-medium text-sm px-8 py-3 hover:bg-blue-400 transition-colors">Start a Project <ArrowRight size={16} /></a>
              <a href="#marketplace" className="inline-flex items-center gap-2 border border-white/20 text-white font-body text-sm px-8 py-3 hover:border-blue-400/40 transition-colors">Open the Marketplace <Briefcase size={16} /></a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-24 bg-[#0F0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body uppercase tracking-[0.2em] text-blue-400 mb-3">What We Build</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-light text-white">Solutions for the <span className="italic text-blue-400">Ambitious.</span></motion.h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => (
              <motion.div key={s.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="bg-white/[0.02] border border-white/5 p-6 group hover:border-blue-400/20 transition-all">
                <s.icon size={24} className="text-blue-400/50 mb-4 group-hover:text-blue-400 transition-colors" strokeWidth={1.5} />
                <h3 className="font-body text-sm font-medium text-white mb-2">{s.title}</h3>
                <p className="text-white/30 font-body text-xs leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Marketplace v2 */}
      <section id="marketplace" className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <MarketplaceV2 />
        </div>
      </section>

      {/* Cross-sell */}
      <section className="py-20 md:py-24 bg-[#0F0F10]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-4">Same Team. Same Excellence.</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-light text-white mb-4">From <span className="italic text-gold">Aristocrats JA</span> to CartBlancJA</motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-white/35 font-body text-sm mb-8 max-w-lg mx-auto">The technology that powers Jamaica's premier event platform is available for your business.</motion.p>
            <Link to="/" className="inline-flex items-center gap-2 border border-gold/30 text-gold font-body text-sm px-8 py-3 hover:bg-gold/10 transition-colors">Explore Aristocrats JA <ArrowRight size={16} /></Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
